import Input from '@blaze-react/input';
import { useToasts } from '@blaze-react/toaster';
import Button from '@blaze-react/button';
import Select from '@blaze-react/select';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { NEWSLETTER_PRODUCT, PRIVACY } from '../../constants';
import {
  checkValidEmail,
  RESIDENCE_COUNTRIES,
  pushToDataLayer,
  submitSalesforceNewsletter,
  userRoleOptions,
  crewRoleOptions
} from '../../utils';

const roleOptions = [...userRoleOptions];
const crewOptions = [...crewRoleOptions];

const countryOfResidenceOptions = RESIDENCE_COUNTRIES.map(country => [country, country]);

const ModalInText = ({
  closeModal,
  toastCaption,
  passedEmail,
  title,
  salesForceCampaignId,
  children
}) => {
  const [submitState, setSubmitState] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(null);
  const { addToast } = useToasts();
  const [values, setValues] = useState({
    additionalTermsAccepted: null,
    countryOfResidence: '',
    firstName: '',
    lastName: '',
    email: passedEmail,
    role: '',
    termsAccepted: false
  });

  const {
    additionalTermsAccepted,
    countryOfResidence,
    firstName,
    lastName,
    email,
    role,
    termsAccepted
  } = values;

  const roleValid = role && role !== 'Please choose...';
  const termsValid = RESIDENCE_COUNTRIES.includes(countryOfResidence) && !!termsAccepted;
  const isFormFull = !!(
    additionalTermsAccepted !== null &&
    firstName &&
    lastName &&
    roleValid &&
    checkValidEmail(email) &&
    termsValid
  );

  const buttonClass = classnames('newsletter-signup__submit', {
    'newsletter-signup__submit--disabled': !isFormFull
  });

  const handleChange = ({ target: { name: targetName } }, value) => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      [targetName]: value
    });
  };

  const handleCountryOfResidenceChange = value => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence: value,
      firstName,
      lastName,
      email,
      role,
      termsAccepted
    });
  };

  const handleTermsCheckboxChange = () => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted: !termsAccepted
    });
  };

  const handleAdditionalTermsChange = val => {
    setValues({
      additionalTermsAccepted: val,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted
    });
  };

  useEffect(() => {
    async function performSubmission() {
      if (submitState === null) return;

      setSubmitState(null);

      try {
        await submitSalesforceNewsletter(
          submitState.values.firstName,
          submitState.values.lastName,
          submitState.values.email,
          [NEWSLETTER_PRODUCT],
          {
            country: submitState.values.countryOfResidence,
            additionalTermsAccepted: submitState.values.additionalTermsAccepted
          },
          submitState.values.role,
          salesForceCampaignId
        );

        const basePayload = submitState.values.role
          ? { user: { role: submitState.values.role } }
          : {};

        pushToDataLayer({
          ...basePayload,
          event: 'Newsletter Signup',
          newsletter_name: NEWSLETTER_PRODUCT
        });

        addToast(toastCaption, {
          appearance: 'success',
          autoDismiss: true
        });
        setSubmissionSuccess(true);

        if (!children || (Array.isArray(children) && !children.length)) closeModal();
      } catch ({ message }) {
        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        });
      }

      setValues({
        additionalTermsAccepted: null,
        countryOfResidence: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        termsAccepted: false
      });
    }
    performSubmission();
  }, [addToast, submitState, setValues, closeModal, salesForceCampaignId, children]);

  return (
    <div className="newsletter-signup__overlay">
      <div className="newsletter-signup__modal newsletter-signup__modal--high">
        <div role="button" className="newsletter-signup__close" onClick={() => closeModal()}>
          <i className="fas fa-times" />
        </div>

        {submissionSuccess && children}

        {!submissionSuccess && (
          <>
            <h2 className="newsletter-signup__title">{title}</h2>

            <form className="newsletter-signup__body">
              <div className="newsletter-signup__first-and-last-name">
                <Input
                  type="text"
                  name="firstName"
                  placeholder="* First name"
                  className="newsletter-signup__input"
                  onChange={({ event, value }) => handleChange(event, value)}
                  value={firstName}
                />

                <Input
                  type="text"
                  name="lastName"
                  placeholder="* Last name"
                  className="newsletter-signup__input"
                  onChange={({ event, value }) => handleChange(event, value)}
                  value={lastName}
                />
              </div>

              <Input
                type="text"
                name="email"
                placeholder="* Email"
                className="newsletter-signup__input"
                onChange={({ event, value }) => handleChange(event, value)}
                value={email}
              />

              <p className="newsletter-signup__headline">* What is your country of residence?</p>

              <Select
                name="countryOfResidence"
                className="newsletter-signup__select"
                options={countryOfResidenceOptions}
                onChange={({ value }) => handleCountryOfResidenceChange(value)}
                selected={countryOfResidence}
              />

              <p className="newsletter-signup__headline">
                * Which category best describes your involvement in superyachting?
              </p>

              <div className="newsletter-signup__role-selection">
                <Select
                  label="Crew"
                  name="role"
                  className="newsletter-signup__select"
                  options={crewOptions}
                  onChange={({ event, value }) => handleChange(event, value)}
                  selected={role}
                />

                <Select
                  label="Other"
                  name="role"
                  className="newsletter-signup__select"
                  options={roleOptions}
                  onChange={({ event, value }) => handleChange(event, value)}
                  selected={role}
                />
              </div>

              <div className="newsletter-signup__checkbox">
                <input
                  readOnly
                  className="newsletter-signup__checkbox__box"
                  type="checkbox"
                  checked={termsAccepted}
                  name="termsAccepted"
                  onClick={() => handleTermsCheckboxChange()}
                />

                <label
                  className="newsletter-signup__checkbox__label"
                  onClick={() => handleTermsCheckboxChange()}>
                  I agree to the{' '}
                  <a
                    href={PRIVACY.TERMS_HREF}
                    className="newsletter-signup__checkbox__footer-link"
                    target="_blank"
                    rel="noopener noreferrer">
                    terms and conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={PRIVACY.HREF}
                    className="newsletter-signup__checkbox__footer-link"
                    target="_blank"
                    rel="noopener noreferrer">
                    privacy notice
                  </a>
                  .
                </label>
              </div>

              <div className="newsletter-signup__radio">
                <label className="newsletter-signup__radio__label">
                  I would like to receive tailored information about events and products from BOAT
                  International Media Ltd, an Informa Group Company. You can opt out at any time by
                  clicking unsubscribe or contacting us. For Further details, see our
                  <a
                    href={PRIVACY.HREF}
                    className="newsletter-signup__checkbox__footer-link"
                    target="_blank"
                    rel="noopener noreferrer">
                    privacy notice
                  </a>
                  .
                </label>

                <div className="newsletter-signup__radio__boxes">
                  <input
                    readOnly
                    type="radio"
                    name="additionalTermsAccepted"
                    value="yes"
                    className="newsletter-signup__radio__box"
                    checked={additionalTermsAccepted === true}
                    onClick={() => handleAdditionalTermsChange(true)}
                  />{' '}
                  <label
                    className="newsletter-signup__radio__box-label"
                    onClick={() => handleAdditionalTermsChange(true)}>
                    Yes
                  </label>{' '}
                  <input
                    readOnly
                    type="radio"
                    name="additionalTermsAccepted"
                    value="no"
                    className="newsletter-signup__radio__box"
                    checked={additionalTermsAccepted === false}
                    onClick={() => handleAdditionalTermsChange(false)}
                  />{' '}
                  <label
                    className="newsletter-signup__radio__box-label"
                    onClick={() => handleAdditionalTermsChange(false)}>
                    No
                  </label>
                </div>
              </div>

              <Button
                type="button"
                name="button"
                data-gtm-id="newsletter-signup"
                className={buttonClass}
                disabled={!isFormFull}
                onClick={() => setSubmitState({ values, setValues })}>
                Submit
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

ModalInText.propTypes = {
  closeModal: PropTypes.func.isRequired,
  passedEmail: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  toastCaption: PropTypes.string.isRequired,
  salesForceCampaignId: PropTypes.string,
  children: PropTypes.node.isRequired
};

ModalInText.defaultProps = {
  salesForceCampaignId: ''
};

export default ModalInText;
